import { QueryStringParams } from "@clockwise/web-commons/src/util/post-message-common.util";
import { getPreCookie } from "@clockwise/web-commons/src/util/pre-cookie.util";
import { useEffect } from "react";
import { setActiveViewEvent, setCalendarUrl } from "../state/actions/chrome-extension.actions";
import { getReduxStore } from "../state/redux-store";
import { TrackingEvents, track } from "../util/analytics.util";
import { logger } from "../util/logger.util";
import { PostMessageManager } from "../util/post-message.util";

export const useCalendarUrlListener = (
  calendarUrl: string,
  chromeExtensionOpen: boolean,
  windowUserEmail: string,
) => {
  // Important Note: Consider deleting all of this. It requires investigations, but it's possible
  // that this functionality is no longer useful since GCal Urls strip out the EventId query param
  // `eid` after it loads in, and clicking an event on a another persons calendar no longer shows an `eid`
  // If the premise above holds true, then this Listener is useless
  // Keeping this code for now since the above premise needs to be fully validated
  useEffect(() => {
    if (!calendarUrl || calendarUrl.indexOf("?") === -1) {
      return;
    }

    // if we're not onboarding, maybe the calendar url has query params for other ui to show
    const search = calendarUrl.split("?")[1];
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(QueryStringParams.EventId)) {
      const eventIdBase64 = searchParams.get(QueryStringParams.EventId);

      if (eventIdBase64) {
        const [externalEventId, calendarId] = atob(eventIdBase64).split(" ");
        if (externalEventId && calendarId) {
          // https://calendar.google.com/calendar/u/0/r/week?eid=Zm9jdXN0aW1lY2Q5NTZlMGFkMTFkNDI0ZDkzMWIwOTY0NDJhZjI2YTc5ZWMyZDBhMWJkOGIzZmMyOWMwNWZiNzNjNjk0OTIxOCBuaWtpdGFAd2lzZWRldnIuY29t
          if (!chromeExtensionOpen) {
            PostMessageManager.toggleSidebar(true, false);
          }

          // We need to set currentViewOfEventWasSetByUrlParam to `true` so that
          // the other view logic (like `detectIdle`) can react to it properly
          // Note that setActiveViewEvent's currentViewOfEventWasSetByUrlParam arg is false by default
          // Thus if user clicks and changes event view, the currentViewOfEventWasSetByUrlParam flag will be set back to false
          // And the idle detection logic will be react accordingly.
          const currentViewOfEventWasSetByUrlParam = true;
          setActiveViewEvent(
            externalEventId,
            calendarId,
            false,
            false,
            false,
            currentViewOfEventWasSetByUrlParam,
          );
        }
      }
    }

    if (searchParams.has(QueryStringParams.Open) && !chromeExtensionOpen) {
      // if we are not open, open
      PostMessageManager.toggleSidebar(true, false);
      PostMessageManager.clearQueryParams(QueryStringParams.Open);
    }
    if (searchParams.has(QueryStringParams.InstallEvent)) {
      // google tag manager
      const dataLayer = window && (window as any).dataLayer;
      if (dataLayer) {
        dataLayer.push({ event: "chromeExtensionInstalled" });
        // new GTM event for user login, dont delete one above until we confirm everything works correctly
        dataLayer.push({
          event: "funnel_step",
          universal_step: "UNIQUE1",
          funnel_name: "chrome_funnel",
          step_name: "install_extension",
          step_number: "2",
        });
      } else {
        logger.error("failed to push event to dataLayer");
      }
      const preCookie = getPreCookie();
      track(TrackingEvents.CHROME_EXTENSION.INSTALLED, {
        windowUserEmail: windowUserEmail,
        latestUtms: preCookie.latestUtms,
      });
      PostMessageManager.clearQueryParams(QueryStringParams.InstallEvent);
    }

    // check if we should open chrome extension
    if (/[?&]cwopen=/.test(window.location.search)) {
      const newPath = window.location.href.replace(/[?&]cwopen=[^&]+/, "");
      window.history.replaceState({ path: newPath }, "", newPath);
      PostMessageManager.openSidebar();
    }

    getReduxStore().dispatch(setCalendarUrl(""));
  }, [calendarUrl]);
};
